import {getDefaultProvider, Web3Provider} from '@ethersproject/providers';
import React, {useContext, useEffect} from 'react';
import {AmasaStakingService} from '../../services/AmasaStakingService';
import {Button, Divider} from '@mui/material';
import LabelText from '../label-text/label-text';
import amasaImage from '../../images/amasa.png';

import styles from './amasa-staking.module.css';
import AmasaProgress from '../bordered-linear-progress/amasa-progress';
import {LoadingContext, LoadingItem} from '../../context/loader';
import {TokenService} from '../../services/TokenService';
import {useWeb3React} from '@web3-react/core';
import moment from "moment";
import {MessageContext} from "../../context/messaging";
import {StakingType} from "../../services/LiquidyMiningService";

function AmasaStaking(props: any) {
    const {library, account} = useWeb3React();
    const {loadingData, refresh, setLoadingData, setRefresh} = useContext(
        LoadingContext,
    );
    const [dataModel, setDataModel] = React.useState({
        name: 'AmasaStaking_' + props.stakingType,
        poolStatus: '',
        stakedBalancePretty: 0,
        stakedBalance: 0,
        progress: '0',
        withdrawDate: 0,
        stakeApprove: false,
        balance: null,
        stakingDuration: 0,
        stakingCapacity: '',
        stakedTotal: '',
        stakingStarts: 0,
        stakingEnds: 0,
        rewardPerc: '0',
        timeStaked: 0,
        stakingType: props.stakingType
    });

    const {
        setMessageOpen,
        setSeverity,
        setMessageText
    } = useContext(MessageContext);

    useEffect(() => {
        fetchData(account, library);
    }, [account, library, refresh]);

    async function unStake() {
        loadingData.set('amasa-unStake', {message: 'Unstaking...'});
        setLoadingData(new Map<string, LoadingItem>(loadingData));
        let hasError = false;

        let tx = await AmasaStakingService(library, props.stakingType).withdraw().catch((error) => {
            console.log(error);
            setMessageOpen(true);
            setSeverity("error");
            setMessageText('UnStaking failed.');
            hasError = true;
        });

        if (tx !== undefined) {
            await tx.wait().catch((error: any) => {
                console.log(error);
                setMessageOpen(true);
                setSeverity("error");
                setMessageText('UnStaking failed.');
                hasError = true;
            });
        }
        loadingData.delete('amasa-unStake');
        setLoadingData(new Map<string, LoadingItem>(loadingData));
        if (!hasError) {
            setRefresh('AmasaUnStaking_' + props.stakingType + "_" + new Date().getTime());
            setMessageOpen(true);
            setSeverity("success");
            setMessageText('UnStaking successfully done.');
        }
    }

    function isUnStakeEnable() {
        const now = moment().unix();
        return Number(dataModel.stakedBalance) > 0 && Number(dataModel.progress) > 0 && Number(dataModel.stakedTotal) > 0 && dataModel.withdrawDate !== 0 && now > dataModel.withdrawDate;
    }

    function isStakeEnable() {
        return Number(dataModel.balance) > 0 && dataModel.poolStatus === 'OPEN';
    }

    return (
        <div className={`${styles.container}`}>
            <div className={`${styles.header}`}>
                <div className={`${styles.title}`}>{props.title}</div>
                <div className={`${styles.stakingWithImage}`}>
                    <div className={`${styles.amasaIcon}`}>
                        <img className={`${styles.amasaIcon}`} alt="" src={amasaImage}/>
                    </div>
                    {/*<div className={`${styles.stakingText}`}>Staking</div>*/}
                </div>
            </div>
            <div className={`${styles.apyContainer}`}>
                <div className={`${styles.apyLabel}`}>APY</div>
                <div
                    className={`${styles.apyText}`}>{dataModel.stakingDuration === 0 ? 0 : Math.round(Number(dataModel.rewardPerc) * 365 / dataModel.stakingDuration)}%
                </div>
            </div>
            <div className={`${styles.labelTextContainer}`}>
                <LabelText
                    label="Full Maturity Period"
                    text={dataModel.stakingDuration + ' days'}
                />
            </div>
            <div className={`${styles.labelTextContainer}`}>
                <LabelText
                    label="Pool Size"
                    text={ Number(dataModel.stakingCapacity) / 1000 + ' million AMAS'}
                />
            </div>
            <div className={`${styles.labelTextContainer}`}>
                <LabelText label="Pool Status" text={dataModel.poolStatus}/>
            </div>
            <div className={`${styles.statusText}`}>
                {props.stakingType === StakingType.FIRST ? 'Closes after 5 days or if full' : 'Pool opens on Feb 7 at 06:00 AM UTC. Pool closes after 5 days or when full. '}
            </div>
            <div className={`${styles.labelTextContainer}`}>
                <LabelText
                    label="Your Stake"
                    text={dataModel.stakedBalancePretty.toString()}
                />
            </div>
            <div className={`${styles.progress}`}>
                <AmasaProgress
                    borderRadius={50}
                    height={32}
                    value={Number(dataModel.progress)}
                />
            </div>
            <div className={`${styles.progressFooter}`}>
                {props.stakingType === StakingType.FIRST ? 'Progress' : 'Your 60 day lockup progress:'} {dataModel.progress} /
                100%
            </div>
            <div className={`${styles.withdrawText}`}>
                {dataModel.withdrawDate === null || dataModel.withdrawDate === 0
                    ? ''
                    : 'Withdrawal date: ' + moment.unix(dataModel.withdrawDate).format('DD, MMMM, YYYY h:mm')}
            </div>
            <div className={`${styles.divider}`}>
                <Divider/>
            </div>
            <div className={`${styles.actionButtons}`}>
                <div
                    className={
                        isStakeEnable()
                            ? `${styles.stakeButtonEnable}`
                            : `${styles.stakeButtonDisable}`
                    }
                >
                    <Button
                        className={`${styles.buttonText}`}
                        onClick={() => {
                            props.handleStakingDialogOpen(dataModel);
                        }}
                        disabled={!isStakeEnable()}
                    >
                        Stake Now
                    </Button>
                </div>
                <div
                    className={
                        isUnStakeEnable()
                            ? `${styles.unStakeButtonEnable}`
                            : `${styles.unStakeButtonDisable}`
                    }
                >
                    <Button
                        className={`${styles.buttonText}`}
                        onClick={unStake}
                        disabled={!isUnStakeEnable()}
                    >
                        Unstake
                    </Button>
                </div>
            </div>
        </div>
    );

    async function fetchData(
        account: string | null | undefined,
        provider: Web3Provider,
    ) {
        if (refresh !== '' && !refresh.includes('AmasaStaking_' + props.stakingType + "_")) {
            return;
        }
        loadingData.set('AmasaStakingLoadData', {message: 'Loading staking info'});
        setLoadingData(new Map<string, LoadingItem>(loadingData));

        let model = {
            name: 'AmasaStaking_' + props.stakingType,
            poolStatus: '',
            stakedBalancePretty: 0,
            stakedBalance: 0,
            progress: '0',
            withdrawDate: 0,
            stakeApprove: false,
            balance: null,
            stakingDuration: 0,
            stakingCapacity: '',
            stakedTotal: '',
            stakingStarts: 0,
            stakingEnds: 0,
            rewardPerc: '0',
            timeStaked: 0,
            stakingType: props.stakingType
        };

        const correctNetwork = process.env.REACT_APP_NETWORK_ID || '1';
        const stakingInfo = await AmasaStakingService((correctNetwork === '4' ? getDefaultProvider('rinkeby') : getDefaultProvider()), props.stakingType).getContractInfo();
        model.stakingDuration = stakingInfo.stakingDuration;
        model.stakingCapacity = stakingInfo.stakingCapacity;
        model.stakedTotal = stakingInfo.stakedTotal;
        model.stakingStarts = stakingInfo.stakingStarts;
        model.stakingEnds = stakingInfo.stakingEnds;
        model.rewardPerc = stakingInfo.rewardPerc;
        model.poolStatus = stakingInfo.poolStatus;

        if (account === undefined) {
            console.log('no account');
            loadingData.delete('AmasaStakingLoadData');
            setLoadingData(new Map<string, LoadingItem>(loadingData));
            setDataModel(model);
            return;
        }
        if (provider !== undefined) {
            const correctNetwork = process.env.REACT_APP_NETWORK_ID || '1';
            const network = await provider.getNetwork();
            if ('' + network.chainId !== correctNetwork) {
                loadingData.delete('AmasaStakingLoadData');
                setLoadingData(new Map<string, LoadingItem>(loadingData));
                return;
            }
        }
        TokenService(provider)
            .geBalance(account, process.env.REACT_APP_TOKEN_ADDRESS || '')
            .then(balance => {
                model.balance = balance;
                AmasaStakingService(provider, props.stakingType)
                    .getStakedBalance(account, true)
                    .then(stakedBalance => {
                        model.stakedBalancePretty = stakedBalance.stakedBalancePretty;
                        model.stakedBalance = Number(stakedBalance.stakedBalance);
                        AmasaStakingService(provider, props.stakingType)
                            .getTimeStaked(account)
                            .then(ts => {
                                model.progress = ts.progress;
                                model.withdrawDate = ts.withdrawDate as number;
                                AmasaStakingService(provider, props.stakingType)
                                    .checkApproval(account, process.env.REACT_APP_TOKEN_ADDRESS || '')
                                    .then(approval => {
                                        model.stakeApprove =
                                            approval == null ? false : approval;
                                        setDataModel(model);
                                        if (props.getCurrentStaking() === model.name) {
                                            props.handleSetDialogData(model);
                                        }
                                        loadingData.delete('AmasaStakingLoadData');
                                        setLoadingData(new Map<string, LoadingItem>(loadingData));
                                    });
                            });
                    });
            })
            .catch(err => {
                console.log(err.toString());
                console.log(err);
                loadingData.delete('AmasaStakingLoadData');
                setLoadingData(new Map<string, LoadingItem>(loadingData));
            });
    }
}

export default AmasaStaking;
